import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Navbar } from '../../components';
import './Foundations.css';

const parts = [
  {
    partLabel: 'Part I',
    partTitle: 'The Next Operational Abstraction',
    articles: [
      {
        slug: 'behavioral-intelligence',
        title: 'Making Autonomous Software Understandable',
        abstract: 'Software engineering has repeatedly evolved its operational abstractions: debuggers, APM, distributed tracing. Autonomous software demands another shift. Why behavioral intelligence — not better tracing — is the next operational lens, and how learned behavioral patterns compound into proactive governance for enterprise agent fleets.',
        readTime: '20 min read',
      },
    ],
  },
  {
    partLabel: 'Part II',
    partTitle: 'Runtime Intelligence',
    articles: [
      {
        slug: 'session-native',
        title: 'Session-Native Runtime Intelligence',
        abstract: 'Why the session — not the trace — should be the primary object in AI agent observability. A thesis on execution lineage, LLM-powered causal reasoning, and the architecture of autonomous systems — validated in production across hundreds of daily agent sessions.',
        readTime: '22 min read',
      },
    ],
  },
  {
    partLabel: 'Part III',
    partTitle: 'Context',
    articles: [
      {
        slug: 'context-engineering',
        title: 'Context Engineering for Agentic Systems',
        abstract: 'Context construction, not task decomposition, is the primary optimization target for scalable multi-agent systems. Introducing Context Locality as a systems principle, validated across production observability and educational intelligence systems.',
        readTime: '24 min read',
      },
    ],
  },
  {
    partLabel: 'Part IV',
    partTitle: 'Evaluation',
    articles: [
      {
        slug: 'eval-frameworks',
        title: 'Evaluation Architecture for AI Systems',
        abstract: 'Evaluation is infrastructure, not afterthought. A framework addressing reliability, faithfulness, and drift as first-class engineering concerns — validated across runtime intelligence and multi-modal educational assessment, with novel methods for causal graph testing, consensus divergence, and counterfactual sensitivity.',
        readTime: '24 min read',
      },
    ],
  },
  {
    partLabel: 'Part V',
    partTitle: 'Governance',
    articles: [
      {
        slug: 'agent-governance',
        title: 'Agent Governance at the Tool-Call Boundary',
        abstract: 'Governance for autonomous agents must be embedded at the execution boundary — the tool call — where reasoning becomes irreversible action. Recursive loop detection, multi-model handoff policies, and explainability as enforcement mechanisms — validated in production with graduated response across hundreds of daily sessions.',
        readTime: '22 min read',
      },
    ],
  },
];

const Foundations = () => {
  const navigate = useNavigate();

  return (
    <div className="foundations">
      <Navbar />
      <div className="foundations__container">

        <div className="foundations__header">
          <button
            className="foundations__home-link"
            onClick={() => navigate('/')}
            type="button"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>CurvSort</span>
          </button>
          <h1 className="foundations__title">Foundations</h1>
          <p className="foundations__intro">
            Making autonomous software understandable. These publications document
            the architectural theses, design decisions, and systems principles behind
            LexiLensAI &mdash; from the operational abstraction that motivates the work,
            through runtime intelligence, context engineering, evaluation, and governance.
          </p>
        </div>

        <div className="foundations__list">
          {parts.map((part) => (
            <div key={part.partLabel} className="foundations__part">
              <div className="foundations__part-header">
                <span className="foundations__part-label">{part.partLabel}</span>
                <span className="foundations__part-title">{part.partTitle}</span>
              </div>
              {part.articles.map((article) => (
                <div
                  key={article.slug}
                  className="foundations__card"
                  onClick={() => navigate(`/foundations/${article.slug}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(`/foundations/${article.slug}`);
                  }}
                >
                  <h2 className="foundations__card-title">{article.title}</h2>
                  <p className="foundations__card-abstract">{article.abstract}</p>
                  <div className="foundations__card-footer">
                    <span className="foundations__card-readtime">{article.readTime}</span>
                    <span className="foundations__card-arrow">
                      <FontAwesomeIcon icon={faArrowRight} />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default Foundations;
